import { useCallback, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { StaticPropertyKey } from '@bvi/api-interfaces/entity/case-property';

import {
  isAllHealthcareOrganizationTypeOption,
  resolveAllHealthcareOrganizationTypes,
} from '../all-healthcare-organization-types';
import {
  isAllSchoolsOrganizationTypeOption,
  resolveAllSchoolsOrganizationTypes,
} from '../all-schools-organization-types';
import {
  dedupeOrganizationTypeValues,
  mapOrganizationTypeValues,
} from '../organization-type-options.shared';
import { isSyntheticOrganizationTypeOption } from '../prepend-organization-type-bulk-options';
import { ISearchFormDataSchema, OnOptionsLoad } from '../types';

export function useOrganizationTypeBulkSelection(onOptionsLoad: OnOptionsLoad) {
  const { control, setValue } = useFormContext<ISearchFormDataSchema>();

  const organizationTypeValue = useWatch({
    control,
    name: StaticPropertyKey.ORGANIZATION_TYPE,
  });

  const previousValueReference = useRef<
    Array<{ id: number; name: string }> | undefined
  >(undefined);
  const isResolvingReference = useRef(false);

  const resolveBulkSelection = useCallback(async () => {
    if (isResolvingReference.current) {
      return;
    }

    isResolvingReference.current = true;

    try {
      const currentValue = organizationTypeValue as
        | Array<{ id: number; name: string }>
        | undefined;

      const hasAllSchools = (currentValue ?? []).some((item) =>
        isAllSchoolsOrganizationTypeOption(item),
      );
      const hasAllHealthcare = (currentValue ?? []).some((item) =>
        isAllHealthcareOrganizationTypeOption(item),
      );

      let resolved = mapOrganizationTypeValues(
        (currentValue ?? []).filter(
          (item) => !isSyntheticOrganizationTypeOption(item),
        ),
      );

      if (hasAllSchools) {
        resolved = await resolveAllSchoolsOrganizationTypes(
          onOptionsLoad,
          resolved,
        );
      }

      if (hasAllHealthcare) {
        resolved = await resolveAllHealthcareOrganizationTypes(
          onOptionsLoad,
          resolved,
        );
      }

      resolved = dedupeOrganizationTypeValues(resolved);

      setValue(StaticPropertyKey.ORGANIZATION_TYPE, resolved, {
        shouldDirty: true,
        shouldValidate: true,
      });
      previousValueReference.current = resolved;
    } finally {
      isResolvingReference.current = false;
    }
  }, [onOptionsLoad, organizationTypeValue, setValue]);

  useEffect(() => {
    const currentValue = organizationTypeValue as
      | Array<{ id: number; name: string }>
      | undefined;

    if (!currentValue || !Array.isArray(currentValue)) {
      previousValueReference.current = currentValue;
      return;
    }

    const hasBulkOption = currentValue.some((item) =>
      isSyntheticOrganizationTypeOption(item),
    );

    const previousValueString = JSON.stringify(previousValueReference.current);
    const currentValueString = JSON.stringify(currentValue);
    const isNewSelection = previousValueString !== currentValueString;

    if (hasBulkOption && isNewSelection && !isResolvingReference.current) {
      previousValueReference.current = currentValue;
      void resolveBulkSelection();
      return;
    }

    previousValueReference.current = currentValue;
  }, [organizationTypeValue, resolveBulkSelection]);
}
